import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { FileRecordNew, FileRecordTypeEnum, FileStatusEnum, PathNode } from '../types/file-record';
import { isFolderType } from '../features/files/file-utils';

type FilesActions =
    | 'DELETE'
    | 'REPLACE_LINK'
    | 'NEW_REFERENCES'
    | 'MOVE'
    | 'CHECK_IN'
    | 'RENAME'
    | 'DOWNLOAD'
    | 'DUPLICATE'
    | 'MAKE_ORIGINAL'
    | 'NEW_LINK'
    | 'REPLACE_DOCUMENT'
    | 'FYI_VIEW';

type RecordStates = {
    count: number;
    current: FileRecordNew | undefined;
    _all: FileRecordNew[];
    toDownload: FileRecordNew[];
    toDuplicate: FileRecordNew[];
    toMakeOriginal: FileRecordNew[];
    toCheckIn: FileRecordNew[];
    toDelete: FileRecordNew[];
    toMove: FileRecordNew[];
    toResyncFYI: FileRecordNew[];
    toReplace: FileRecordNew | undefined;
    toMultiReference: FileRecordNew | undefined;
    toRename: FileRecordNew | undefined;
    toFindOriginal: FileRecordNew | undefined;
    toViewInFYI: FileRecordNew | undefined;
};

export type FilesPageState = {
    value: {
        action: FilesActions | undefined;
        files: RecordStates;
        currentPath: PathNode[] | undefined;
    };
};

const initialState: FilesPageState = {
    value: {
        action: undefined,
        files: {
            count: 0,
            current: undefined,
            _all: [],
            toDownload: [],
            toDuplicate: [],
            toMakeOriginal: [],
            toCheckIn: [],
            toDelete: [],
            toMove: [],
            toReplace: undefined,
            toMultiReference: undefined,
            toRename: undefined,
            toFindOriginal: undefined,
            toViewInFYI: undefined,
            toResyncFYI: [],
        },
        currentPath: undefined,
    },
};

export const filesPageSlice = createSlice({
    name: 'filesPage',
    initialState,
    reducers: {
        setPath: (state, action: PayloadAction<PathNode[]>) => {
            state.value.currentPath = action.payload;
            return state;
        },
        selectFiles: (state, action: PayloadAction<FileRecordNew[]>) => {
            const files = action.payload;
            state.value.files = {
                count: files.length,
                current: files.length === 0 ? files[0] : undefined,
                _all: files,
                toDownload: getAvailableToDownload(files),
                toDuplicate: getAvailableToDuplicate(files),
                toMakeOriginal: getAvailableToMakeOriginal(files),
                toCheckIn: getAvailableToCheckIn(files),
                toDelete: getAvailableToDelete(files),
                toMove: getAvailableToMove(files),
                toReplace: getAvailableToReplace(files),
                toMultiReference: getAvailableToReference(files),
                toRename: getAvailableToRename(files),
                toFindOriginal: getAvailableToFindOriginal(files),
                toViewInFYI: getAvailableToViewInFYI(files),
                toResyncFYI: getAvailableToResyncFYI(files),
            };

            return state;
        },
        _act: (state, action: PayloadAction<FilesActions | undefined>) => {
            state.value.action = action.payload;
            return state;
        },
    },
});

export const { _act, selectFiles, setPath } = filesPageSlice.actions;

export const selector = (state: RootState): { _action?: FilesActions; records: RecordStates; currentPath?: PathNode[] } => ({
    _action: state.filesPage.value.action,
    records: state.filesPage.value.files,
    currentPath: state.filesPage.value.currentPath,
});

export default filesPageSlice.reducer;

function getAvailableToCheckIn(records: FileRecordNew[]): FileRecordNew[] {
    const unique: FileRecordNew[] = [];
    records
        .filter((r) => r.status === FileStatusEnum.CHECKED_OUT)
        .forEach((r) => {
            if (r.recordType === FileRecordTypeEnum.DOCUMENT) {
                unique.push(r);
            } else if (r.recordType === FileRecordTypeEnum.REFERENCE) {
                if (!unique.find((u) => u.id === r.source?.id)) {
                    unique.push(r);
                }
            }
        });

    return unique;
}

function getAvailableToDownload(records: FileRecordNew[]): FileRecordNew[] {
    const unique: FileRecordNew[] = [];

    records
        .filter(
            (r) =>
                r.recordType === FileRecordTypeEnum.DOCUMENT ||
                r.recordType === FileRecordTypeEnum.REFERENCE ||
                r.recordType === FileRecordTypeEnum.FYI_DOCUMENT
        )
        .forEach((r) => {
            if (r.recordType === FileRecordTypeEnum.DOCUMENT || r.recordType === FileRecordTypeEnum.FYI_DOCUMENT) {
                unique.push(r);
            } else if (r.source?.recordType === FileRecordTypeEnum.DOCUMENT || r.source?.recordType === FileRecordTypeEnum.FYI_DOCUMENT) {
                if (!unique.find((u) => u.id === r.source?.id)) {
                    unique.push(r);
                }
            }
        });

    return unique;
}

function getAvailableToDuplicate(records: FileRecordNew[]): FileRecordNew[] {
    return records.filter((r) => !r.locked && (r.recordType === FileRecordTypeEnum.DOCUMENT || r.recordType === FileRecordTypeEnum.LINK));
}

function getAvailableToMakeOriginal(records: FileRecordNew[]): FileRecordNew[] {
    return records.filter(
        (r) => !r.locked && r.recordType === FileRecordTypeEnum.REFERENCE && r.source?.recordType !== FileRecordTypeEnum.FYI_DOCUMENT
    );
}

function getAvailableToDelete(records: FileRecordNew[]): FileRecordNew[] {
    return records.filter((r) => !r.locked && !isFolderType(r.recordType) && r.status !== FileStatusEnum.CHECKED_OUT);
}

function getAvailableToMove(records: FileRecordNew[]): FileRecordNew[] {
    return records.filter((r) => !r.locked && !isFolderType(r.recordType) && r.recordType !== FileRecordTypeEnum.FYI_DOCUMENT);
}

function getAvailableToReplace(records: FileRecordNew[]): FileRecordNew | undefined {
    if (records.length !== 1) {
        return undefined;
    }
    const record = records[0];

    if (!record.locked && (record.recordType === FileRecordTypeEnum.DOCUMENT || record.recordType === FileRecordTypeEnum.LINK)) {
        return record;
    }
}

function getAvailableToReference(records: FileRecordNew[]): FileRecordNew | undefined {
    if (records.length !== 1) {
        return undefined;
    }
    const record = records[0];

    if (record.locked) {
        return undefined;
    }

    if (
        record.recordType === FileRecordTypeEnum.DOCUMENT ||
        record.recordType === FileRecordTypeEnum.LINK ||
        (record.recordType === FileRecordTypeEnum.FYI_DOCUMENT && record.provider)
    ) {
        return record;
    }
    return undefined;
}

function getAvailableToRename(records: FileRecordNew[]): FileRecordNew | undefined {
    if (records.length !== 1) {
        return undefined;
    }
    const record = records[0];

    if (record.locked) {
        return undefined;
    }
    if (!isFolderType(record.recordType)) {
        return record;
    }
    return undefined;
}

function getAvailableToFindOriginal(records: FileRecordNew[]): FileRecordNew | undefined {
    if (records.length !== 1) {
        return undefined;
    }

    const record = records[0];

    if (record.recordType === FileRecordTypeEnum.REFERENCE) {
        return record;
    }
    return undefined;
}

function getAvailableToViewInFYI(records: FileRecordNew[]): FileRecordNew | undefined {
    if (records.length !== 1) {
        return undefined;
    }

    const record = records[0];

    if (record.recordType === FileRecordTypeEnum.FYI_DOCUMENT || record.source?.recordType === FileRecordTypeEnum.FYI_DOCUMENT) {
        return record;
    }
    return undefined;
}

function getAvailableToResyncFYI(records: FileRecordNew[]): FileRecordNew[] {
    return records.filter((record) => {
        return (
            !record.locked &&
            (record.recordType === FileRecordTypeEnum.FYI_DOCUMENT || record.source?.recordType === FileRecordTypeEnum.FYI_DOCUMENT)
        );
    });
}
